import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Typography,
    Chip,
    TextField,
    CircularProgress,
    RadioGroup,
    Radio,
    FormControl,
    FormLabel,
    FormControlLabel
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import Button from "../../../../shared/OAWorkflow/components/atoms/Button";
import { axios_tenant } from "../../../../../axios";
import { BRAND_COLORS } from "../../../../shared/OAWorkflow/constants/taskStatuses";

const BASE_URL = "/collaboration/workflow";

const toYMD = (d) => {
    const p = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

const presetRange = (days) => {
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - (days - 1));
    return { from: toYMD(from), to: toYMD(to) };
};

const MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const formatDateDDMMMYYYY = (dateStr) => {
    if (!dateStr) return "";
    const parts = dateStr.split("-");
    if (parts.length !== 3) return dateStr;
    const year = parts[0];
    const monthIndex = parseInt(parts[1], 10) - 1;
    const day = parts[2];
    return `${day}-${MONTHS_SHORT[monthIndex] || ""}-${year}`;
};

const DownloadReportModal = ({
    open,
    onClose,
    workType = "Post Grant Support",
    supportTypeFilter = "All",
    clientFilter = "All",
    onSuccess,
    onError
}) => {
    const [reportPreset, setReportPreset] = useState("30");
    const [reportFrom, setReportFrom] = useState(presetRange(30).from);
    const [reportTo, setReportTo] = useState(presetRange(30).to);
    const [statusFilter, setStatusFilter] = useState("All"); // All | Completed | Pending
    const [priorityFilter, setPriorityFilter] = useState("All"); // All | Rush | Standard
    const [reportDownloading, setReportDownloading] = useState(false);

    // Reset fields when opening modal
    useEffect(() => {
        if (open) {
            const r = presetRange(30);
            setReportPreset("30");
            setReportFrom(r.from);
            setReportTo(r.to);
            setStatusFilter("All");
            setPriorityFilter("All");
            setReportDownloading(false);
        }
    }, [open]);

    const applyReportPreset = (preset) => {
        setReportPreset(preset);
        if (preset === "custom") return;
        const days = preset === "today" ? 1 : parseInt(preset, 10);
        const r = presetRange(days);
        setReportFrom(r.from);
        setReportTo(r.to);
    };

    // Guard: 6-month max window on the selected range
    const reportRangeError = (() => {
        if (!reportFrom || !reportTo) return "Select a From and To date";
        const from = new Date(reportFrom);
        const to = new Date(reportTo);
        if (from > to) return "'From Date' cannot be greater than 'To Date'. Please select a valid date range.";
        const maxTo = new Date(from);
        maxTo.setMonth(maxTo.getMonth() + 6);
        if (to > maxTo) return "Reports can only be downloaded for a maximum date range of 6 months. Please select a valid date range.";
        return null;
    })();

    const handleDownloadExcel = async () => {
        if (reportRangeError) {
            if (onError) onError(reportRangeError);
            return;
        }
        try {
            setReportDownloading(true);
            const params = {
                workType,
                dateFrom: reportFrom,
                dateTo: reportTo,
                statusFilter,
                priorityFilter,
                ...(supportTypeFilter && supportTypeFilter !== "All" && { supportType: supportTypeFilter }),
                ...(clientFilter && clientFilter !== "All" && { routedClient: clientFilter })
            };

            const res = await axios_tenant.get(`${BASE_URL}/export-report`, { params, responseType: "blob", silent: true });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement("a");
            link.href = url;
            const prefix = workType === "OA Shell" ? "OA_Shell_Report" : "PGS_Report";
            link.setAttribute("download", `${prefix}_${new Date().toISOString().slice(0, 10)}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            onClose();
            if (onSuccess) onSuccess("Report downloaded successfully!");
        } catch (err) {
            const msg = err?.response?.data?.message || "Failed to download report";
            if (onError) onError(msg);
        } finally {
            setReportDownloading(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="xs"
            fullWidth
            PaperProps={{ sx: { borderRadius: "12px" } }}
        >
            <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1.5, fontWeight: 700, fontSize: "18px", color: "#1E293B" }}>
                <DownloadIcon sx={{ color: BRAND_COLORS.PRIMARY }} />
                <Box>
                    <Box>Download Report</Box>
                    <Typography sx={{ fontSize: "12px", color: "#64748B", fontWeight: 400 }}>{workType}</Typography>
                </Box>
            </DialogTitle>
            <DialogContent sx={{ pt: "8px !important" }}>
                {/* Date Range Section */}
                <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#334155", mb: 1 }}>Date Range</Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
                    {[
                        { key: "today", label: "Today" },
                        { key: "7", label: "Last 7 days" },
                        { key: "30", label: "Last 30 days" },
                        { key: "90", label: "Last 90 days" },
                        { key: "custom", label: "Custom range" }
                    ].map(p => (
                        <Chip
                            key={p.key}
                            label={p.label}
                            onClick={() => applyReportPreset(p.key)}
                            variant={reportPreset === p.key ? "filled" : "outlined"}
                            sx={{
                                fontSize: "12px",
                                ...(reportPreset === p.key
                                    ? { backgroundColor: BRAND_COLORS.PRIMARY, color: "#fff", "&:hover": { backgroundColor: BRAND_COLORS.PRIMARY_HOVER } }
                                    : { color: BRAND_COLORS.PRIMARY, borderColor: BRAND_COLORS.PRIMARY, "&:hover": { backgroundColor: BRAND_COLORS.SECONDARY_HOVER } })
                            }}
                        />
                    ))}
                </Box>

                <Box sx={{ display: "flex", gap: 2, mb: 2.5 }}>
                    <TextField
                        label="From" type="date" size="small" fullWidth
                        InputLabelProps={{ shrink: true }}
                        value={reportFrom}
                        onChange={(e) => { setReportFrom(e.target.value); setReportPreset("custom"); }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                '&.Mui-focused fieldset': {
                                    borderColor: BRAND_COLORS.PRIMARY,
                                },
                            },
                            '& .MuiInputLabel-root.Mui-focused': {
                                color: BRAND_COLORS.PRIMARY,
                            },
                        }}
                    />
                    <TextField
                        label="To" type="date" size="small" fullWidth
                        InputLabelProps={{ shrink: true }}
                        value={reportTo}
                        onChange={(e) => { setReportTo(e.target.value); setReportPreset("custom"); }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                '&.Mui-focused fieldset': {
                                    borderColor: BRAND_COLORS.PRIMARY,
                                },
                            },
                            '& .MuiInputLabel-root.Mui-focused': {
                                color: BRAND_COLORS.PRIMARY,
                            },
                        }}
                    />
                </Box>

                {reportRangeError && (
                    <Typography sx={{ fontSize: "12px", color: "#D32F2F", mb: 2, mt: -1.5 }}>
                        {reportRangeError}
                    </Typography>
                )}

                {/* Radio Group 1: Task Status */}
                <FormControl component="fieldset" sx={{ mb: 2, display: "block" }}>
                    <FormLabel component="legend" sx={{ fontSize: "13px", fontWeight: 600, color: "#334155 !important", mb: 0.5 }}>
                        Status
                    </FormLabel>
                    <RadioGroup
                        row
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                    >
                        <FormControlLabel
                            value="All"
                            control={<Radio size="small" sx={{ color: BRAND_COLORS.PRIMARY, '&.Mui-checked': { color: `${BRAND_COLORS.PRIMARY} !important` } }} />}
                            label={<span style={{ fontSize: "13px", color: "#334155" }}>All</span>}
                        />
                        <FormControlLabel
                            value="Completed"
                            control={<Radio size="small" sx={{ color: BRAND_COLORS.PRIMARY, '&.Mui-checked': { color: `${BRAND_COLORS.PRIMARY} !important` } }} />}
                            label={<span style={{ fontSize: "13px", color: "#334155" }}>Completed</span>}
                        />
                        <FormControlLabel
                            value="Pending"
                            control={<Radio size="small" sx={{ color: BRAND_COLORS.PRIMARY, '&.Mui-checked': { color: `${BRAND_COLORS.PRIMARY} !important` } }} />}
                            label={<span style={{ fontSize: "13px", color: "#334155" }}>Pending</span>}
                        />
                    </RadioGroup>
                </FormControl>

                {/* Radio Group 2: Task Priority */}
                <FormControl component="fieldset" sx={{ mb: 1, display: "block" }}>
                    <FormLabel component="legend" sx={{ fontSize: "13px", fontWeight: 600, color: "#334155 !important", mb: 0.5 }}>
                        Priority
                    </FormLabel>
                    <RadioGroup
                        row
                        value={priorityFilter}
                        onChange={(e) => setPriorityFilter(e.target.value)}
                    >
                        <FormControlLabel
                            value="All"
                            control={<Radio size="small" sx={{ color: BRAND_COLORS.PRIMARY, '&.Mui-checked': { color: `${BRAND_COLORS.PRIMARY} !important` } }} />}
                            label={<span style={{ fontSize: "13px", color: "#334155" }}>All</span>}
                        />
                        <FormControlLabel
                            value="Rush"
                            control={<Radio size="small" sx={{ color: BRAND_COLORS.PRIMARY, '&.Mui-checked': { color: `${BRAND_COLORS.PRIMARY} !important` } }} />}
                            label={<span style={{ fontSize: "13px", color: "#334155" }}>Rush/Priority tasks</span>}
                        />
                        <FormControlLabel
                            value="Standard"
                            control={<Radio size="small" sx={{ color: BRAND_COLORS.PRIMARY, '&.Mui-checked': { color: `${BRAND_COLORS.PRIMARY} !important` } }} />}
                            label={<span style={{ fontSize: "13px", color: "#334155" }}>Standard Tasks</span>}
                        />
                    </RadioGroup>
                </FormControl>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #E2E8F0", display: "block" }}>
                {!reportRangeError && (
                    <Typography sx={{ fontSize: "12px", color: "#64748B", mb: 1.5 }}>
                        {`${formatDateDDMMMYYYY(reportFrom)} – ${formatDateDDMMMYYYY(reportTo)}`}
                    </Typography>
                )}
                <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
                    <Button variant="secondary" onClick={onClose}>Cancel</Button>
                    <Button
                        variant="primary"
                        onClick={handleDownloadExcel}
                        disabled={reportDownloading || !!reportRangeError}
                        startIcon={reportDownloading ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon sx={{ fontSize: 18 }} />}
                    >
                        Download Excel
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
};

export default DownloadReportModal;
